"use client";

import { useState, useEffect } from "react";

interface PolaroidImageProps {
  src: string;
  alt?: string;
  prompt?: string;
  className?: string;
}

/**
 * Polaroid-style image component with developing animation
 * Mimics the classic Polaroid camera print effect
 */
export default function PolaroidImage({ src, alt, prompt, className = "" }: PolaroidImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDeveloping, setIsDeveloping] = useState(true);

  useEffect(() => {
    // Start developing animation after image loads
    if (isLoaded) {
      // Simulate Polaroid developing time (2-3 seconds)
      const developTimer = setTimeout(() => {
        setIsDeveloping(false);
      }, 2500);
      return () => clearTimeout(developTimer);
    }
  }, [isLoaded]);

  return (
    <>
      <style>{`
        @keyframes polaroid-develop {
          0% {
            opacity: 1;
            filter: brightness(2) contrast(0.3);
          }
          30% {
            opacity: 0.8;
            filter: brightness(1.5) contrast(0.5);
          }
          60% {
            opacity: 0.4;
            filter: brightness(1.2) contrast(0.7);
          }
          100% {
            opacity: 0;
            filter: brightness(1) contrast(1);
          }
        }
        
        .polaroid-developing-overlay {
          animation: polaroid-develop 2.5s ease-out forwards;
        }
      `}</style>
      <div
        className={`polaroid-frame ${className}`}
        style={{
          display: "inline-block",
          background: "#ffffff",
          padding: "12px 12px 50px 12px", // Thicker bottom padding for Polaroid effect
          borderRadius: "4px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)",
          transform: "rotate(-1deg)", // Slight rotation for authentic Polaroid look
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          maxWidth: "100%",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "rotate(0deg) scale(1.02)";
          e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "rotate(-1deg) scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)";
        }}
      >
        {/* Image container with developing effect */}
        <div
          className="polaroid-image-container"
          style={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            borderRadius: "2px",
            backgroundColor: "#f5f5f5",
          }}
        >
          {/* Developing overlay - starts white and fades out */}
          <div
            className="polaroid-developing-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#ffffff",
              opacity: isDeveloping ? 1 : 0,
              transition: "opacity 2.5s ease-out",
              zIndex: 2,
              pointerEvents: "none",
            }}
          />
          
          {/* The actual image */}
          <img
            src={src}
            alt={alt || prompt || "Generated image"}
            onLoad={() => setIsLoaded(true)}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.5s ease-in",
              maxHeight: "400px",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Prompt text at bottom (like Polaroid caption) */}
        {prompt && (
          <div
            style={{
              marginTop: "8px",
              paddingTop: "8px",
              fontSize: "11px",
              color: "#666",
              fontFamily: "system-ui, -apple-system, sans-serif",
              textAlign: "center",
              lineHeight: "1.3",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {prompt}
          </div>
        )}
      </div>
    </>
  );
}
